import React from 'react';
import {
  CaptionText,
  FlexColumn,
  formatNumber,
  DEFAULT_AMOUNT_PRECISION,
  DEFAULT_PRECISION,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';

import { CsprAmount } from '../cspr-amount/cspr-amount';
import {
  currencyPrecisionByCase,
  motesToCSPR,
  PrecisionCase,
} from '../../utils/currency';
import Big from 'big.js';
import Skeleton from 'react-loading-skeleton';
import FiatCurrency from '../multi-currency/multi-currency';
import { FTAmountDataType } from '@block-explorer/entities';
import {
  formatTokenAmount,
  ftTokenAmountToCurrency,
  getAmountFromMotes,
} from '../../utils/ft-token-amount';
import { HistoryLink } from '../history-link/history-link';
import FullPrecisionTooltip from '../full-precision-tooltip/full-precision-tooltip';

export interface TableDataCurrencyAmountProps {
  amount: string | undefined | null;
  loading: boolean;
  tokenCurrencyRate?: number;
  outgoingAmount?: boolean;
  cep18AmountData?: Omit<
    FTAmountDataType,
    'totalSupply' | 'totalSupplyMotes'
  > | null;
  isCSPR?: boolean;
  precisionCase?: PrecisionCase;
  purseUrefPath?: string | null;
  showFiatValue?: boolean;
  showSymbol?: boolean;
  shouldShortenAmount?: boolean;
}

const FormatCsprAmount = ({
  amount,
  currencyAmount,
  isAmountInCSPR,
  precisionCase,
  showSymbol,
  showFiatValue,
}) => {
  if (isAmountInCSPR) {
    return (
      <>
        <CsprAmount
          motes={amount}
          precisionCase={precisionCase || PrecisionCase.deployCost}
          hideCsprCurrency={!showSymbol}
        />
        {showFiatValue ? (
          <CaptionText size={2} variation="darkGray">
            <FiatCurrency
              amount={currencyAmount}
              precision={DEFAULT_AMOUNT_PRECISION}
            />
          </CaptionText>
        ) : null}
      </>
    );
  }

  if (amount == 0) {
    return '-';
  }

  return (
    <>
      {formatNumber(amount, { precision: 0 })}
      <CaptionText size={2} variation="darkGray">
        {'-'}
      </CaptionText>
    </>
  );
};

export const FormatFtAmount = ({
  amount,
  cep18AmountData,
  precisionCase,
  purseUrefPath,
  showFiatValue,
  showSymbol,
  shouldShortenAmount,
}) => {
  const calculatedCep18Amount =
    cep18AmountData.decimals != null && amount
      ? getAmountFromMotes(amount, cep18AmountData.decimals)
      : null;

  const formattedCep18Amount = formatTokenAmount(
    calculatedCep18Amount,
    precisionCase ? currencyPrecisionByCase(precisionCase) : DEFAULT_PRECISION,
    shouldShortenAmount
  );

  const tokenAmount = showSymbol
    ? `${formattedCep18Amount} ${cep18AmountData.symbol}`
    : formattedCep18Amount;

  const currencyAmount =
    calculatedCep18Amount && cep18AmountData.price
      ? ftTokenAmountToCurrency(calculatedCep18Amount, cep18AmountData.price)
      : null;

  return (
    <FlexColumn itemsSpacing={4}>
      {purseUrefPath ? (
        <HistoryLink
          href={purseUrefPath}
          ariaDescription={'Link to uref details'}
        >
          <FullPrecisionTooltip
            amount={calculatedCep18Amount}
            symbol={cep18AmountData.symbol}
          >
            {tokenAmount}
          </FullPrecisionTooltip>
        </HistoryLink>
      ) : (
        <FullPrecisionTooltip
          amount={calculatedCep18Amount}
          symbol={cep18AmountData.symbol}
        >
          {tokenAmount}
        </FullPrecisionTooltip>
      )}

      <CaptionText size={2} variation="darkGray">
        <FiatCurrency
          amount={showFiatValue ? currencyAmount : null}
          precision={
            precisionCase
              ? currencyPrecisionByCase(precisionCase)
              : DEFAULT_AMOUNT_PRECISION
          }
          nullLabel={'-'}
        />
      </CaptionText>
    </FlexColumn>
  );
};

export const TableDataCurrencyAmount = ({
  amount,
  cep18AmountData,
  tokenCurrencyRate,
  loading,
  outgoingAmount,
  precisionCase,
  purseUrefPath,
  isCSPR = true,
  showFiatValue = true,
  showSymbol = true,
  shouldShortenAmount = false,
}: TableDataCurrencyAmountProps) => {
  if (loading) {
    return (
      <BodyText size={3}>
        <Skeleton />
      </BodyText>
    );
  }

  if (cep18AmountData?.symbol) {
    return (
      <BodyText size={3} monotype noWrap>
        {amount == null ? (
          '-'
        ) : (
          <FlexColumn>
            {outgoingAmount && '-'}
            <FormatFtAmount
              amount={amount}
              cep18AmountData={cep18AmountData}
              precisionCase={precisionCase}
              purseUrefPath={purseUrefPath}
              showFiatValue={showFiatValue}
              showSymbol={showSymbol}
              shouldShortenAmount={shouldShortenAmount}
            />
          </FlexColumn>
        )}
      </BodyText>
    );
  }

  const csprAmount = amount ? motesToCSPR(amount) : 0;
  const currencyAmount =
    tokenCurrencyRate && csprAmount
      ? Big(csprAmount).mul(tokenCurrencyRate).toString()
      : null;

  return (
    <BodyText size={3} monotype noWrap>
      {amount == null ? (
        '-'
      ) : (
        <FlexColumn>
          {outgoingAmount && '-'}
          <FormatCsprAmount
            amount={amount}
            currencyAmount={currencyAmount}
            isAmountInCSPR={isCSPR}
            precisionCase={precisionCase}
            showSymbol={showSymbol}
            showFiatValue={showFiatValue}
          />
        </FlexColumn>
      )}
    </BodyText>
  );
};
